import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  KubernetesSetupFormData,
  KubernetesSetupPolicyPayload,
  KUBERNETES_SETUP_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is KubernetesSetupPolicyPayload
 */
function isKubernetesSetupPolicyPayload(
  data: unknown
): data is KubernetesSetupPolicyPayload {
  return typeof data === 'object' && data !== null && 'UseLoadBalancer' in data;
}

/**
 * Hook to get initial values for Kubernetes Setup Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useKubernetesSetupInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesSetupFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_SETUP_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesSetupPolicyPayload(data)) {
      return KUBERNETES_SETUP_INITIAL_VALUES;
    }

    return {
      useLoadBalancer: data.UseLoadBalancer?.value ?? false,
      ingressControllers: Array.isArray(data.IngressClasses)
        ? data.IngressClasses.map((ic) => ({ name: ic.value?.name ?? '' }))
        : [],
      allowNoneIngressClass: data.AllowNoneIngressClass?.value ?? false,
      ingressAvailabilityPerNamespace:
        data.IngressAvailabilityPerNamespace?.value ?? false,
      restrictStandardUserIngressW:
        data.RestrictStandardUserIngressW?.value ?? false,
      changeWindow: data.EndpointChangeWindow?.value ?? {
        Enabled: false,
        StartTime: '22:00',
        EndTime: '02:00',
      },
      enableResourceOverCommit: data.EnableResourceOverCommit?.value ?? false,
      resourceOverCommitPercentage:
        data.ResourceOverCommitPercentage?.value ?? 0,
      useServerMetrics: data.UseServerMetrics?.value ?? false,
      storageClasses: Array.isArray(data.StorageClasses)
        ? data.StorageClasses.map((sc) => sc.value)
        : [],
    };
  }, [policy]);
}
